import Vue from 'vue';
import VueRouter from 'vue-router';
import store from '@/store/index';
import { getInstance } from '@/plugins/gAuth';
import gAuthGuard from '@/auth/authGuard';

import defaultLayout from '@/components/appLayout/defaultLayout.vue';
import loginForm from '@/components/appLayout/loginForm.vue';
import termsOfUse from '@/components/appLayout/termsOfUse.vue';
import faqPage from '@/components/appLayout/faqPage.vue';
import playbook from '@/components/playbook/playbook.vue';
import landing from '@/components/playbook/landing.vue';
import playPanel from '@/components/playbook/tables/playPanel.vue';
import playDetails from '@/components/playbook/details/playDetails.vue';
import capecPanel from '@/components/playbook/tables/capecPanel.vue';
import capecDetails from '@/components/playbook/details/capecDetails.vue';
import techniquePanel from '@/components/playbook/tables/techniquePanel.vue';
import techniqueDetails from '@/components/playbook/details/techniqueDetails.vue';
import controlPanel from '@/components/playbook/tables/controlPanel.vue';
import controlDetails from '@/components/playbook/details/controlDetails.vue';
import cwePanel from '@/components/playbook/tables/cwePanel.vue';
import cweDetails from '@/components/playbook/details/cweDetails.vue';
import updatePlaybook from '@/components/forms/updatePlaybook.vue';
import pageNotFound from '@/components/appLayout/pageNotFound.vue';
import pageLoading from '@/components/appLayout/pageLoading.vue';

Vue.use(VueRouter);

const routes = [
  {
    path: '/login',
    name: 'login',
    component: loginForm,
    beforeEnter: gAuthGuard
  },
  {
    path: '/terms-of-use',
    name: 'termsOfUse',
    component: termsOfUse,
    meta: { requiresAuth: true },
    beforeEnter: gAuthGuard
  },
  {
    path: '/',
    name: 'home',
    component: defaultLayout,
    meta: { requiresAuth: true },
    beforeEnter: gAuthGuard
  },
  {
    path: '/faqs',
    name: 'faqs',
    component: faqPage,
    meta: { requiresAuth: true },
    beforeEnter: gAuthGuard
  },
  {
    path: '/playbook',
    component: playbook,
    beforeEnter: gAuthGuard,
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        name: 'playbook',
        component: landing,
        beforeEnter: gAuthGuard,
        meta: { requiresAuth: true }
      },
      {
        path: 'plays',
        name: 'plays',
        component: playPanel,
        beforeEnter: (to, from, next) => {
          gAuthGuard(to, from, next);
            if (to.name === 'plays') {
              store.dispatch('fetchPlays', '?ordering=id');
            }
            next();
        },
        meta: { requiresAuth: true },
        children: [
          {
            path: ':id',
            name: 'play_view',
            component: playDetails,
            beforeEnter: gAuthGuard
          }
        ]
      },
      {
        path: 'capecs',
        name: 'capecs',
        component: capecPanel,
        beforeEnter: (to, from, next) => {
          gAuthGuard(to, from, next);
            if (to.name === 'capecs') {
              store.dispatch('fetchCAPECs', '');
            }
            next();
        },
        meta: { requiresAuth: true },
        children: [
          {
            path: ':id',
            name: 'capec_view',
            component: capecDetails,
            beforeEnter: gAuthGuard
          }
        ]
      },
      {
        path: 'techniques',
        name: 'techniques',
        component: techniquePanel,
        beforeEnter: (to, from, next) => {
          gAuthGuard(to, from, next);
            if (to.name === 'techniques') {
              store.dispatch('fetchTechniques', '?revoked=false&deprecated=false&ordering=mitre_id&no_subs=true');
            }
            next();
        },
        meta: { requiresAuth: true },
        children: [
          {
            path: ':id',
            name: 'technique_view',
            component: techniqueDetails,
            beforeEnter: gAuthGuard
          }
        ]
      },
      {
        path: 'controls',
        name: 'controls',
        component: controlPanel,
        beforeEnter: (to, from, next) => {
          gAuthGuard(to, from, next);
            if (to.name === 'controls') {
              store.dispatch('fetchControls', '?ordering=sort_id&type=SP800-53');
            }
            next();
        },
        meta: { requiresAuth: true },
        children: [
          {
            path: ':id',
            name: 'control_view',
            component: controlDetails,
            beforeEnter: gAuthGuard
          }
        ]
      },
      {
        path: 'cwes',
        name: 'cwes',
        component: cwePanel,
        beforeEnter: (to, from, next) => {
          gAuthGuard(to, from, next);
            if (to.name === 'cwes') {
              store.dispatch('fetchCWEs', '?ordering=id');
            }
            next();
        },
        meta: { requiresAuth: true },
        children: [
          {
            path: ':id',
            name: 'cwe_view',
            component: cweDetails,
            beforeEnter: gAuthGuard
          }
        ]
      }
    ]
  },
  {
    path: '/contribute',
    name: 'contribute',
    component: updatePlaybook,
    beforeEnter: gAuthGuard,
    meta: { requiresAuth: true }
  },
  {
    path: '/callback',
    name: 'authRedirect',
    component: pageLoading,
    beforeEnter: gAuthGuard
  },
  {
    path: '/django-login',
    name: 'djangoLogin',
    component: pageLoading
  },
  { path: '*', component: pageNotFound }
];

const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  routes
});

const originalPush = VueRouter.prototype.push;
VueRouter.prototype.push = function push(location) {
  return originalPush.call(this, location).catch((err) => {
    if (err.name !== 'NavigationDuplicated') throw err;
  });
};

router.beforeEach(async (to, from, next) => {
  const authService = getInstance();
  const isOffline = process.env.VUE_APP_OFFLINE === 'TRUE';

  const fn = async () => {
    if (to.matched.some((record) => record.meta.requiresAuth)) {
      if (!authService.googleSignedIn && !authService.isSignedIn) {
        return next('/login');
      }
      if (!authService.isSignedIn && to.path !== '/django-login') {
        return next('/django-login');
      }
      if (from.path === '/callback'
        || from.path === '/django-login'
        || to.path === '/terms-of-use') {
        store.commit('setLayout', 'terms-of-use-layout');
      } else if (store.getters.layout !== 'default-layout') {
        store.commit('setLayout', 'default-layout');
      }
      if (to.path === '/') return next('/playbook');
      if (to.name === 'contribute'
        && (!store.getters.groups.includes('USER_GROUP') || isOffline)) {
        store.commit('setLayout', 'auth-layout');
      }
      next();
    } else {
      if (to.path === '/login') store.commit('setLayout', 'login-layout');
      else if (to.path === '/callback') store.commit('setLayout', 'loading-layout');
      else if (to.path === '/django-login') {
        try {
          await authService.getDjangoToken();
          return next('/terms-of-use');
        } catch {
          return next('/login');
        }
      } else store.commit('setLayout', 'err-layout');
      return next();
    }
  };

  if (!authService.loading) {
    return fn();
  }

  authService.$watch('loading', (loading) => {
    if (loading === false) {
      return fn();
    }
  });
});

export default router;
